import { useEffect } from 'react';
import { useFormState } from 'react-hook-form';
import { FormContainer } from 'react-hook-form-mui';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { AlertError } from 'Common/AlertError';
import { EmailField } from 'Common/Form';
import { ForgotPasswordSchema } from 'Common/Form/ValidationSchemas/ForgotPasswordSchema';
import { StyledAuthFormContainer, StyledButton } from 'Components/Styled';
import { useAppForm } from 'Utils/customHooks';

import type { IForgotPasswordRequest } from '@/libs/models';

const formFieldNames = {
  email: 'email'
} as const;

type Props = {
  email?: string;
  onSubmit: (formData: IForgotPasswordRequest) => void;
  isSubmitSuccess?: boolean;
  error?: string | null;
};

export const ForgotPasswordForm = ({
  email,
  onSubmit,
  isSubmitSuccess = false,
  error
}: Readonly<Props>) => {
  const { t } = useTranslation();
  const formContext = useAppForm<IForgotPasswordRequest>(ForgotPasswordSchema());
  const { isValid } = useFormState({ control: formContext.control });

  useEffect(() => {
    if (email) {
      formContext.setValue(formFieldNames.email, email, { shouldValidate: true });
    }
  }, [email, formContext.setValue]);

  useEffect(() => {
    if (error) {
      toast.error(t(error));
    }
  }, [error, t]);

  if (!isSubmitSuccess) {
    return (
      <FormContainer onSuccess={onSubmit} formContext={formContext}>
        <StyledAuthFormContainer>
          {error && <AlertError text={t(error)} />}

          <EmailField name={formFieldNames.email} />

          <StyledButton type="submit" size="large" disabled={!isValid}>
            {t('auth.resetPassword')}
          </StyledButton>
        </StyledAuthFormContainer>
      </FormContainer>
    );
  }

  return (
    <div>
      {t('auth.resetPasswordMessagePart1')} {formContext.control._formValues.email}
      {t('auth.resetPasswordMessagePart2')}
    </div>
  );
};
